// src/components/LeadForm.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Form, Field, useFormState } from 'react-final-form';
import { Lead, LeadApiResponse, LeadStatus } from 'models/Lead';
import { Button, InputField } from 'components';
import { useLeadMutations } from '../../hooks';
import { Tooltip } from 'react-tooltip';
import { FiCopy } from 'react-icons/fi';
import classNames from 'classnames';
import { useTanstackMutation } from 'hooks';
import { queryClient } from 'react-query';
import { API_PATHS } from 'api';
import {
  composeValidators,
  EmailRegExp,
  validateRegex,
  validateRequired,
} from 'validations';
import { DenyLeadButton } from '../LeadEditor/components';
import { FaSync } from 'react-icons/fa';
import { isLeadPersonal } from 'utils/lead';

import './LeadForm.styles.scss';

export type LeadFormValues = {
  email: string;
  subject: string;
  message: string;
  first_name: string;
  last_name: string;
  submitAction: 'save' | 'saveAndConfirm';
};

export type LeadFormProps = {
  lead: Lead;
  onSuccess?: () => void;
};

const LeadForm: React.FC<LeadFormProps> = ({ lead, onSuccess }) => {
  const [copyTooltip, setCopyTooltip] = useState('Copy text');
  const isPersonal = isLeadPersonal(lead);

  const initialValues: Partial<LeadFormValues> = {
    email: (isPersonal ? lead?.email : lead?.contact?.email) || '',
    subject:
      (isPersonal ? lead?.first_subject : lead?.contact?.firstSubject) || '',
    message:
      (isPersonal ? lead?.first_outreach : lead?.contact?.firstOutreach) || '',
    ...(isPersonal ? lead : lead?.contact),
  };

  const { executeLeadMutations, isUpdating, isConfirming } = useLeadMutations(
    lead,
    onSuccess,
    isPersonal,
  );

  const handleSubmit = useCallback(
    async (values: LeadFormValues) => {
      await executeLeadMutations(lead?._id, lead.contact, values);
      onSuccess?.();
    },
    [executeLeadMutations, lead?._id, lead?.contact, onSuccess],
  );

  const { mutate: generateEmailMutation, isPending: isPendingGenerateEmail } =
    useTanstackMutation({
      onSuccess: (response: LeadApiResponse) => {
        queryClient.setQueryData(
          [`${isPersonal ? 'personal-' : ''}lead`, { id: lead?._id }],
          (old: any) => {
            return old
              ? {
                  ...old,
                  ...(isPersonal ? response : { lead: response }),
                }
              : old;
          },
        );

        queryClient.invalidateQueries({
          queryKey: [`${isPersonal ? 'personal-' : ''}leads`],
        });
        queryClient.invalidateQueries({
          queryKey: [`${isPersonal ? 'personal-' : ''}tabs`],
        });
      },
      onError: console.error,
    });

  const selectLead = () =>
    generateEmailMutation({
      path: isPersonal
        ? API_PATHS.personalLeads.generateOutreachEmail(lead?._id)
        : API_PATHS.leads.generateEmail(lead?._id),
      method: 'post',
    });

  return (
    <div className="contact-lead-container">
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, form, valid, pristine }) => {
          return (
            <form onSubmit={handleSubmit} className="contact-lead-form">
              {/* Email, First Name, Last Name, Subject fields */}
              <div className="form-field">
                <Field
                  name="email"
                  label="Email"
                  component={InputField}
                  placeholder="Email"
                  validate={composeValidators(
                    validateRequired('Email is required.'),
                    validateRegex(
                      'Please enter a valid email address.',
                      EmailRegExp,
                    ),
                  )}
                />
              </div>
              <div className="row">
                <div className="form-field">
                  <Field
                    name="first_name"
                    label="First Name"
                    component={InputField}
                    placeholder="First Name"
                  />
                </div>
                <div className="form-field">
                  <Field
                    name="last_name"
                    label="Last Name"
                    component={InputField}
                    placeholder="Last Name"
                  />
                </div>
              </div>
              <div className="row">
                <div className="form-field">
                  <Field
                    name="subject"
                    label="Subject"
                    component={InputField}
                    placeholder="Subject"
                    validate={validateRequired('Subject is required')}
                  />
                </div>
                <Button
                  type="button"
                  className="generate-email-btn"
                  onClick={selectLead}
                  isLoading={isPendingGenerateEmail}
                >
                  {lead?.contact?.firstOutreach ? (
                    <>
                      <FaSync />
                      Regenerate Email
                    </>
                  ) : (
                    'Generate Email'
                  )}
                </Button>
              </div>

              <div className="form-field">
                <div className="field-label-container">
                  <label>Message</label>
                  <Button
                    onClick={() => {
                      const message = form.getState().values.message;
                      if (message) {
                        const blobHTML = new Blob([message], {
                          type: 'text/html',
                        });
                        const blobText = new Blob([message], {
                          type: 'text/plain',
                        });
                        navigator.clipboard
                          .write([
                            new ClipboardItem({
                              'text/html': blobHTML,
                              'text/plain': blobText,
                            }),
                          ])
                          .then(() => {
                            setCopyTooltip('Copied!');
                            setTimeout(() => setCopyTooltip('Copy text'), 2000);
                          })
                          .catch(console.error);
                      }
                    }}
                    data-tooltip-id="copy-tooltip"
                    data-tooltip-content={copyTooltip}
                    data-tooltip-place="top"
                    variant="icon"
                    className="contact-lead-form__copy-btn"
                  >
                    <FiCopy />
                  </Button>
                  <Tooltip id="copy-tooltip" />
                </div>
                <Field
                  name="message"
                  validate={validateRequired('Email content is required')}
                >
                  {({ input, meta }) => {
                    const { values } = useFormState<Record<string, any>>();
                    const [localValue, setLocalValue] = useState('');

                    const first = values.first_name || '';
                    const last = values.last_name || '';
                    const shouldTransform = !!first;

                    useEffect(() => {
                      const rawValue = input.value || '';
                      const replaced = shouldTransform
                        ? rawValue
                            .replace(/{{firstName}}/g, first)
                            .replace(/{{lastName}}/g, last)
                        : rawValue;
                      setLocalValue(replaced);
                    }, [input.value, first, last, shouldTransform]);

                    const handleChange = (
                      e: React.ChangeEvent<HTMLTextAreaElement>,
                    ) => {
                      const newValue = e.target.value;
                      setLocalValue(newValue);

                      const template = shouldTransform
                        ? newValue
                            .replace(new RegExp(first, 'g'), '{{firstName}}')
                            .replace(new RegExp(last, 'g'), '{{lastName}}')
                        : newValue;

                      input.onChange(template);
                    };

                    return (
                      <>
                        <textarea
                          id="textarea"
                          className="textarea"
                          disabled={!lead?.contact}
                          placeholder="Write your message here..."
                          rows={12}
                          value={localValue}
                          onChange={handleChange}
                          onBlur={input.onBlur}
                        />
                        {meta.touched && meta.error && (
                          <span className="error">{meta.error}</span>
                        )}
                      </>
                    );
                  }}
                </Field>
              </div>

              {/* Action buttons */}
              {(lead?.contact || isPersonal) && (
                <div className="contact-lead-form__action-btns">
                  {lead?.contact?.leadStatus !== LeadStatus.DECLINED ? (
                    <DenyLeadButton leadId={lead._id} onSuccess={onSuccess} />
                  ) : (
                    <div />
                  )}
                  <div>
                    <Button
                      type="submit"
                      onClick={() => form.change('submitAction', 'save')}
                      isLoading={isUpdating}
                      variant="outline"
                      className={classNames(
                        'submit-button',
                        'contact-lead-form__save-btn',
                      )}
                    >
                      Save
                    </Button>
                    <Button
                      type="submit"
                      onClick={() =>
                        form.change('submitAction', 'saveAndConfirm')
                      }
                      isLoading={isConfirming}
                      className={classNames(
                        'submit-button',
                        'contact-lead-form__confirm-btn',
                      )}
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
              )}
            </form>
          );
        }}
      />
    </div>
  );
};

export default LeadForm;
